import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { listRbacConfig, updateRbacPermission } from '../../api/api';
import LoadingIndicator from '../../components/Common/LoadingIndicator';
import Alert from '../../components/UI/Alert';
import PrimaryButton from '../../components/Common/PrimaryButton';
import PageTitle from '../../components/Layout/PageTitle';
import { 
    Save, 
    Eye, 
    Lock, 
    Check, 
    X, 
    Search, 
    Sparkles,
    LayoutDashboard,
    Users,
    Calendar,
    Pill,
    Package,
    MapPin,
    Settings,
    NotebookPen,
    Building2,
    ClipboardList,
    FileText,
    FolderOpen,
    Wallet,
    Shield
} from 'lucide-react';

// --- Constantes ---
const AVAILABLE_ROLES = ['admin', 'proprietario', 'gerente', 'medico', 'enfermeiro', 'fisioterapeuta', 'cuidador', 'familiar'];

// Mapeamento de módulos com ícones e categorias
const MODULE_CONFIG = {
    dashboard: { 
        label: 'Dashboard', 
        icon: LayoutDashboard, 
        category: 'Geral',
        description: 'Painel principal e visão geral'
    },
    usuarios: { 
        label: 'Usuários', 
        icon: Users, 
        category: 'Gestão',
        description: 'Gerenciamento de usuários e equipe'
    },
    pacientes: { 
        label: 'Pacientes', 
        icon: Users, 
        category: 'Atendimento',
        description: 'Cadastro e gestão de pacientes'
    },
    agendamentos: { 
        label: 'Agendamentos', 
        icon: Calendar, 
        category: 'Atendimento',
        description: 'Agendamento de consultas e visitas'
    },
    medicamentos: { 
        label: 'Medicamentos', 
        icon: Pill, 
        category: 'Atendimento',
        description: 'Controle de medicamentos'
    },
    equipamentos: { 
        label: 'Equipamentos', 
        icon: Package, 
        category: 'Atendimento',
        description: 'Gestão de equipamentos médicos'
    },
    check_in_out: { 
        label: 'Check In/Out', 
        icon: MapPin, 
        category: 'Atendimento',
        description: 'Registro de entrada e saída'
    },
    config: { 
        label: 'Configurações', 
        icon: Settings, 
        category: 'Gestão',
        description: 'Configurações do sistema'
    },
    diario: { 
        label: 'Diário de Cuidados', 
        icon: NotebookPen, 
        category: 'Atendimento',
        description: 'Registro diário de cuidados'
    },
    atendimentos: { 
        label: 'Atendimentos', 
        icon: ClipboardList, 
        category: 'Atendimento',
        description: 'Histórico de atendimentos'
    },
    organizacao: { 
        label: 'Organização', 
        icon: Building2, 
        category: 'Gestão',
        description: 'Dados da organização'
    },
    planos_medicos: { 
        label: 'Planos Médicos', 
        icon: FileText, 
        category: 'Atendimento',
        description: 'Planos de medicação'
    },
    estoque: { 
        label: 'Estoque', 
        icon: Package, 
        category: 'Gestão',
        description: 'Controle de estoque'
    },
    agenda_tarefas: { 
        label: 'Agenda de Tarefas', 
        icon: ClipboardList, 
        category: 'Atendimento',
        description: 'Tarefas agendadas'
    },
    prontuario: { 
        label: 'Prontuário', 
        icon: FileText, 
        category: 'Atendimento',
        description: 'Prontuários eletrônicos'
    },
    documentos_paciente: { 
        label: 'Documentos do Paciente', 
        icon: FolderOpen, 
        category: 'Atendimento',
        description: 'Documentos e arquivos'
    },
    financeiro: { 
        label: 'Financeiro', 
        icon: Wallet, 
        category: 'Gestão',
        description: 'Gestão financeira'
    },
    ia: { 
        label: 'Assistente de IA', 
        icon: Sparkles, 
        category: 'Gestão',
        description: 'Inteligência artificial assistente'
    }
};

const MODULE_KEYS = Object.keys(MODULE_CONFIG);
const ACTION_KEYS = ['can_create', 'can_edit', 'can_delete']; 
const READ_ACTION_KEY = 'can_read';

// Agrupar módulos por categoria
const getModulesByCategory = () => {
    const categories = {};
    MODULE_KEYS.forEach(key => {
        const category = MODULE_CONFIG[key].category;
        if (!categories[category]) {
            categories[category] = [];
        }
        categories[category].push(key);
    });
    return categories;
};

// --- Utils ---
const transformConfigListToMatrix = (configList) => {
    const matrix = {};
    const allActionKeys = [READ_ACTION_KEY, ...ACTION_KEYS];

    AVAILABLE_ROLES.forEach(role => {
        matrix[role] = {};
        MODULE_KEYS.forEach(moduleKey => {
            matrix[role][moduleKey] = {
                can_access: false,
                actions: allActionKeys.reduce((acc, action) => ({ ...acc, [action]: false }), {})
            };
        });
    });

    configList.forEach(item => {
        if (matrix[item.role] && matrix[item.role][item.module_key]) {
            const dbActions = Object.entries(item.actions || {}).reduce((acc, [key, value]) => {
                acc[key] = value === 1;
                return acc;
            }, {});

            matrix[item.role][item.module_key] = {
                can_access: item.can_access === 1,
                actions: dbActions
            };
        }
    });
    return matrix;
};

// --- Componente Principal ---
const RbacConfigPage = () => {
    const [originalConfig, setOriginalConfig] = useState([]);
    const [editMatrix, setEditMatrix] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('Todos');

    const modulesByCategory = useMemo(() => getModulesByCategory(), []);
    const categories = useMemo(() => ['Todos', ...Object.keys(modulesByCategory)], [modulesByCategory]);

    // Lista filtrada de módulos
    const filteredModuleList = useMemo(() => {
        let modules = MODULE_KEYS.map(key => ({
            key,
            ...MODULE_CONFIG[key]
        }));

        // Filtro por categoria
        if (selectedCategory !== 'Todos') {
            modules = modules.filter(module => 
                modulesByCategory[selectedCategory]?.includes(module.key)
            );
        }

        // Filtro por texto
        const normalizedFilter = filterText.trim().toLowerCase();
        if (normalizedFilter) {
            modules = modules.filter(module => 
                module.label.toLowerCase().includes(normalizedFilter) ||
                module.description.toLowerCase().includes(normalizedFilter)
            );
        }

        return modules;
    }, [filterText, selectedCategory, modulesByCategory]);

    const fetchConfig = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await listRbacConfig();
            setOriginalConfig(response.data.data);
            setEditMatrix(transformConfigListToMatrix(response.data.data));
        } catch (err) {
            console.error("Erro ao buscar RBAC:", err);
            setError(err.response?.data?.message || 'Falha ao carregar as configurações de RBAC.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchConfig();
    }, [fetchConfig]);

    const handlePermissionChange = (role, moduleKey, type, actionKey) => {
        setEditMatrix(prevMatrix => {
            const newMatrix = JSON.parse(JSON.stringify(prevMatrix));
            const moduleConfig = newMatrix[role]?.[moduleKey];

            if (!moduleConfig) return prevMatrix;

            if (type === 'can_access') {
                const newAccess = !moduleConfig.can_access;
                moduleConfig.can_access = newAccess;
                moduleConfig.actions[READ_ACTION_KEY] = newAccess; 

                if (!newAccess) {
                    ACTION_KEYS.forEach(key => moduleConfig.actions[key] = false);
                }
            } else if (type === 'action' && actionKey) {
                const newActionValue = !moduleConfig.actions[actionKey];
                moduleConfig.actions[actionKey] = newActionValue;

                if (newActionValue) {
                    moduleConfig.can_access = true;
                    moduleConfig.actions[READ_ACTION_KEY] = true;
                }
            }
            return newMatrix;
        });
    };

    const normalizeActions = (actions) => {
        if (!actions || typeof actions !== 'object') return {};
        
        const normalized = {};
        const allActions = [READ_ACTION_KEY, ...ACTION_KEYS];
        
        allActions.forEach(action => {
            const value = actions[action];
            normalized[action] = (value === true || value === 1 || value === '1') ? 1 : 0;
        });
        
        return normalized;
    };

    const actionsAreEqual = (actions1, actions2) => {
        const norm1 = normalizeActions(actions1);
        const norm2 = normalizeActions(actions2);
        
        const allActions = [READ_ACTION_KEY, ...ACTION_KEYS];
        return allActions.every(action => norm1[action] === norm2[action]);
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        setSuccessMessage(null);

        const changedPermissions = [];

        AVAILABLE_ROLES.forEach(role => {
            MODULE_KEYS.forEach(moduleKey => {
                const newConfig = editMatrix[role]?.[moduleKey];
                
                if (!newConfig) return; 

                const originalItem = originalConfig.find(item => 
                    item.role === role && item.module_key === moduleKey
                ) || { can_access: 0, actions: {} };

                const newCanAccess = newConfig.can_access ? 1 : 0;
                const originalCanAccess = originalItem.can_access === 1 || originalItem.can_access === true ? 1 : 0;

                const newActionsForDb = normalizeActions(newConfig.actions);
                const originalActionsNormalized = normalizeActions(originalItem.actions);

                const viewChanged = newCanAccess !== originalCanAccess;
                const actionsChanged = !actionsAreEqual(newActionsForDb, originalActionsNormalized);

                if (viewChanged || actionsChanged) {
                    changedPermissions.push({
                        role,
                        module_key: moduleKey,
                        can_access: newCanAccess,
                        actions: newActionsForDb
                    });
                }
            });
        });

        if (changedPermissions.length === 0) {
            setSuccessMessage("Nenhuma alteração para salvar.");
            setIsSaving(false);
            return;
        }

        try {
            const results = await Promise.allSettled(
                changedPermissions.map(item => updateRbacPermission(item))
            );
            
            const errors = results.filter(result => result.status === 'rejected');
            if (errors.length > 0) {
                console.error('[RBAC CONFIG] Erros ao salvar permissões:', errors);
                throw new Error(`${errors.length} permissão(ões) falharam ao salvar. Verifique os logs.`);
            }

            setSuccessMessage(`✅ ${changedPermissions.length} permissão(ões) salva(s) com sucesso!`);
            
            await fetchConfig(); 
        } catch (err) {
            console.error("Erro ao salvar RBAC:", err);
            setError(err.response?.data?.message || 'Falha ao salvar as configurações. Verifique os logs.');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return <LoadingIndicator message="Carregando matriz de permissões RBAC..." />;
    }

    if (error && !Object.keys(editMatrix).length) {
        return <Alert type="error" message={error} />;
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto">
                <PageTitle 
                    title="Gestão de Permissões"
                    subtitle="Configure as permissões de acesso para cada perfil de usuário da sua organização"
                />

                {/* Alertas e Botão Salvar */}
                <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1">
                        {successMessage && (
                            <Alert type="success" message={successMessage} onClose={() => setSuccessMessage(null)} />
                        )}
                        {error && (
                            <Alert type="error" message={error} onClose={() => setError(null)} />
                        )}
                    </div>
                    <PrimaryButton
                        onClick={handleSave}
                        disabled={isSaving}
                        variant="primary"
                        className="flex items-center justify-center gap-2 min-w-[180px]"
                    >
                        <Save className="w-4 h-4" />
                        {isSaving ? 'Salvando...' : 'Salvar Alterações'}
                    </PrimaryButton>
                </div>

                {/* Filtros */}
                <div className="mb-6 space-y-4">
                    {/* Filtro por categoria */}
                    <div className="flex flex-wrap gap-2">
                        {categories.map(category => (
                            <button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                                    selectedCategory === category
                                        ? 'bg-emerald-600 text-white shadow-md'
                                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                                }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>

                    {/* Campo de busca */}
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="w-5 h-5 text-gray-400" /> 
                        </div>
                        <input
                            type="text"
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            placeholder="Buscar módulo por nome ou descrição..."
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                    </div>
                </div>

                {/* Grid de Módulos - Desktop */}
                <div className="hidden md:block">
                    {filteredModuleList.length > 0 ? (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredModuleList.map(moduleItem => {
                                const IconComponent = moduleItem.icon;
                                return (
                                    <div key={moduleItem.key} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden flex flex-col">
                                        {/* Cabeçalho do Card */}
                                        <div className="px-6 py-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-emerald-100 rounded-lg">
                                                    <IconComponent className="w-5 h-5 text-emerald-700" />
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="text-lg font-bold text-gray-900">
                                                        {moduleItem.label}
                                                    </h3>
                                                    <p className="text-xs text-gray-600 mt-0.5">
                                                        {moduleItem.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Lista de Roles */}
                                        <div className="divide-y divide-gray-100 flex-1">
                                            {AVAILABLE_ROLES.map(role => {
                                                const config = editMatrix[role]?.[moduleItem.key];
                                                if (!config) return null; 
                                                
                                                const isCreateOrEditActive = config.actions.can_create || config.actions.can_edit;
                                                const actionKeyForCreateEdit = config.actions.can_create ? 'can_create' : 'can_edit';

                                                return (
                                                    <div key={role} className="p-5 hover:bg-gray-50 transition-colors">
                                                        <div className="flex justify-between items-center mb-3">
                                                            <span className="text-sm font-semibold text-gray-800 capitalize">
                                                                {role === 'proprietario' ? 'Proprietário' : 
                                                                 role === 'admin' ? 'Administrador' :
                                                                 role === 'gerente' ? 'Gerente' :
                                                                 role === 'medico' ? 'Médico' :
                                                                 role === 'enfermeiro' ? 'Enfermeiro' :
                                                                 role === 'fisioterapeuta' ? 'Fisioterapeuta' :
                                                                 role === 'cuidador' ? 'Cuidador' :
                                                                 role === 'familiar' ? 'Familiar' : role}
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                                            <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                                                                <span className="text-xs text-gray-500">Permissões:</span>
                                                                <div className="flex items-center gap-1 flex-wrap">
                                                                    <PermissionBadge
                                                                        active={config.can_access}
                                                                        label="Acesso"
                                                                        icon={Lock}
                                                                    />
                                                                    {config.can_access && (
                                                                        <>
                                                                            <PermissionBadge
                                                                                active={config.actions.can_read}
                                                                                label="Leitura"
                                                                                icon={Eye}
                                                                            />
                                                                            <PermissionBadge
                                                                                active={isCreateOrEditActive}
                                                                                label="Editar"
                                                                                icon={Check}
                                                                            />
                                                                            <PermissionBadge
                                                                                active={config.actions.can_delete}
                                                                                label="Deletar"
                                                                                icon={X}
                                                                            />
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                                <PermissionToggle
                                                                    isActive={config.can_access}
                                                                    onChange={() => handlePermissionChange(role, moduleItem.key, 'can_access')}
                                                                    Icon={Lock}
                                                                    tooltip="Acesso"
                                                                />
                                                                <PermissionToggle
                                                                    isActive={config.actions.can_read}
                                                                    onChange={() => handlePermissionChange(role, moduleItem.key, 'action', READ_ACTION_KEY)}
                                                                    Icon={Eye}
                                                                    tooltip="Leitura"
                                                                    disabled={!config.can_access}
                                                                />
                                                                <PermissionToggle
                                                                    isActive={isCreateOrEditActive}
                                                                    onChange={() => handlePermissionChange(role, moduleItem.key, 'action', actionKeyForCreateEdit)}
                                                                    Icon={Check}
                                                                    tooltip="Criar/Editar"
                                                                    disabled={!config.can_access}
                                                                />
                                                                <PermissionToggle
                                                                    isActive={config.actions.can_delete}
                                                                    onChange={() => handlePermissionChange(role, moduleItem.key, 'action', 'can_delete')}
                                                                    Icon={X}
                                                                    tooltip="Deletar"
                                                                    disabled={!config.can_access}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="bg-white shadow rounded-lg p-8 text-center">
                            <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-500 text-lg">
                                Nenhum módulo encontrado com o termo "{filterText}".
                            </p>
                        </div>
                    )}
                </div>

                {/* Cards Mobile */}
                <div className="block md:hidden space-y-4">
                    {filteredModuleList.length > 0 ? (
                        filteredModuleList.map(moduleItem => {
                            const IconComponent = moduleItem.icon;
                            return (
                                <div key={moduleItem.key} className="bg-white rounded-xl shadow-md overflow-hidden">
                                    <div className="px-4 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-emerald-100 rounded-lg">
                                                <IconComponent className="w-5 h-5 text-emerald-700" />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-lg font-bold text-gray-900">
                                                    {moduleItem.label}
                                                </h3>
                                                <p className="text-xs text-gray-600 mt-0.5">
                                                    {moduleItem.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="divide-y divide-gray-100">
                                        {AVAILABLE_ROLES.map(role => {
                                            const config = editMatrix[role]?.[moduleItem.key];
                                            if (!config) return null; 
                                            
                                            const isCreateOrEditActive = config.actions.can_create || config.actions.can_edit;
                                            const actionKeyForCreateEdit = config.actions.can_create ? 'can_create' : 'can_edit';

                                            return (
                                                <div key={role} className="p-5">
                                                    <div className="flex justify-between items-center mb-3">
                                                        <span className="text-sm font-semibold text-gray-800 capitalize">
                                                            {role === 'proprietario' ? 'Proprietário' : 
                                                             role === 'admin' ? 'Administrador' :
                                                             role === 'gerente' ? 'Gerente' :
                                                             role === 'medico' ? 'Médico' :
                                                             role === 'enfermeiro' ? 'Enfermeiro' :
                                                             role === 'fisioterapeuta' ? 'Fisioterapeuta' :
                                                             role === 'cuidador' ? 'Cuidador' :
                                                             role === 'familiar' ? 'Familiar' : role}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-col gap-3">
                                                        <div className="flex flex-col gap-1.5">
                                                            <span className="text-xs text-gray-500">Permissões:</span>
                                                            <div className="flex items-center gap-1 flex-wrap">
                                                                <PermissionBadge
                                                                    active={config.can_access}
                                                                    label="Acesso"
                                                                    icon={Lock}
                                                                />
                                                                {config.can_access && (
                                                                    <>
                                                                        <PermissionBadge
                                                                            active={config.actions.can_read}
                                                                            label="Leitura"
                                                                            icon={Eye}
                                                                        />
                                                                        <PermissionBadge
                                                                            active={isCreateOrEditActive}
                                                                            label="Editar"
                                                                            icon={Check}
                                                                        />
                                                                        <PermissionBadge
                                                                            active={config.actions.can_delete}
                                                                            label="Deletar"
                                                                            icon={X}
                                                                        />
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <PermissionToggle
                                                                isActive={config.can_access}
                                                                onChange={() => handlePermissionChange(role, moduleItem.key, 'can_access')}
                                                                Icon={Lock}
                                                                tooltip="Acesso"
                                                            />
                                                            <PermissionToggle
                                                                isActive={config.actions.can_read}
                                                                onChange={() => handlePermissionChange(role, moduleItem.key, 'action', READ_ACTION_KEY)}
                                                                Icon={Eye}
                                                                tooltip="Leitura"
                                                                disabled={!config.can_access}
                                                            />
                                                            <PermissionToggle
                                                                isActive={isCreateOrEditActive}
                                                                onChange={() => handlePermissionChange(role, moduleItem.key, 'action', actionKeyForCreateEdit)}
                                                                Icon={Check}
                                                                tooltip="Criar/Editar"
                                                                disabled={!config.can_access}
                                                            />
                                                            <PermissionToggle
                                                                isActive={config.actions.can_delete}
                                                                onChange={() => handlePermissionChange(role, moduleItem.key, 'action', 'can_delete')}
                                                                Icon={X}
                                                                tooltip="Deletar"
                                                                disabled={!config.can_access}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="bg-white shadow rounded-lg p-8 text-center">
                            <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-500 text-lg">
                                Nenhum módulo encontrado com o termo "{filterText}".
                            </p>
                        </div>
                    )}
                </div>

                {/* Nota informativa */}
                <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-blue-800">
                            <p className="font-semibold mb-1">💡 Dica:</p>
                            <p>
                                A permissão <strong>"Acesso"</strong> é a base. Quando desativada, todas as outras permissões (Leitura, Editar, Deletar) são automaticamente desativadas. 
                                Ative primeiro o acesso para então configurar as permissões específicas.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Componente de Badge de Permissão
const PermissionBadge = ({ active, label, icon: Icon }) => {
    if (!active) return null;
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
            <Icon className="w-3 h-3" />
            {label}
        </span>
    );
};

// Componente de Toggle de Permissão
const PermissionToggle = ({ isActive, onChange, Icon, tooltip, className, disabled = false }) => {
    const baseClasses = "rounded-lg p-2.5 transition-all duration-200 shadow-sm flex justify-center items-center min-w-[36px] min-h-[36px]";
    const activeClasses = isActive 
        ? "bg-emerald-600 hover:bg-emerald-700 text-white" 
        : "bg-gray-200 hover:bg-gray-300 text-gray-600";
    const disabledClasses = disabled 
        ? "opacity-40 cursor-not-allowed" 
        : "cursor-pointer hover:scale-105";

    return (
        <button
            onClick={onChange}
            className={`${baseClasses} ${activeClasses} ${disabledClasses} ${className || ''}`}
            title={tooltip}
            disabled={disabled}
        >
            <Icon className="w-4 h-4" />
        </button>
    );
};

export default RbacConfigPage;
